import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback, useRef} from "react";
import StreamCountdown from "../../components/streaming/StreamCountdown";
import StreamingUnavailable from "../../components/streaming/StreamingUnavailable";
import StreamingEnd from "../../components/streaming/StreamingEnd";
import StreamNotStarted from "../../components/streaming/StreamNotStarted";
import { usePoster } from "../../hooks/usePoster";
import { useAuth } from "../../context/AuthProvider";
import { socket } from "../../components/mediasoup/socket";
import { recordingSocket } from "../../components/mediasoup/recordingSocket";
import logger from "../../utils/logger";
import SecureStreamPlayer, {
  type StreamPlayerHandle,
} from "../../components/streaming/StreamPlayer";
import api from "../../lib/api";

interface StreamData {
  streamEndAt?: string;
  streamPath: string;
  streamStartAt: string;
  streamStatus: boolean;
  currentTime: number;
  highCourtName: string;
  courtName: string;
  status: string;
  streamingStatus: string;
}

interface WatchedByInfo {
  sessionId: string;
  userId: string | undefined;
  ip: string;
  latitude: any;
  longitude: any;
  date: Date;
}

interface VideoPlayerProps {
  cnr?: string;
  date?: string;
}

const VideoPlayer = ({ cnr, date: propDate }: VideoPlayerProps = {}) => {
  const { user, userLocation } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const post = usePoster();

  const CNR = cnr || (location.state?.cnr as string | undefined);
  const date = propDate || (location.state?.date as string | undefined);

  const ip = user?.ip || "0.0.0.0";
  const mobile = user?.phone || "";

  const [watchedBy, setWatchedBy] = useState<WatchedByInfo | null>(null);
  const [data, setData] = useState<StreamData | null>(null);
  const [isLive, setIsLive] = useState(false);
  const [streamEnded, setStreamEnded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [isLeft, setIsLeft] = useState(false);
  const [hlsToken, setHlsToken] = useState<string>("");

  const playerRef = useRef<StreamPlayerHandle>(null);
  const hasRunRef = useRef(false);

  /* ---------------------------------------------------------
      PAUSE / PLAY VIDEO ELEMENT WHEN isPaused CHANGES
      — must be before any return so hook order is stable
  --------------------------------------------------------- */
  useEffect(() => {
    if (!playerRef.current || !isLive) return;
    if (isPaused) {
      playerRef.current.pause(); // also mutes → kills audio leak
    } else {
      playerRef.current.play(); // unmutes + seeks to live edge
    }
  }, [isPaused, isLive]);

  /* ---------------------------------------------------------
      BUILD WATCHED-BY PAYLOAD
  --------------------------------------------------------- */
  useEffect(() => {
    if (hasRunRef.current || !user || !CNR || !ip || !userLocation) return;
    setWatchedBy({
      sessionId: CNR,
      userId: user.id,
      ip,
      latitude: userLocation.lat,
      longitude: userLocation.lon,
      date: date ? new Date(date) : new Date(),
    });
    hasRunRef.current = true;
  }, [user, CNR, ip, userLocation, date]);

  /* ---------------------------------------------------------
      FETCH STREAM STATUS
  --------------------------------------------------------- */
  const fetchStreamStatus = useCallback(async () => {
    try {
      setLoading(true);
      const res = await post(`/api/search/streaming-status`, { cnr: CNR });
      logger.log("📡 Stream status response:", res);
      if (res.status && res.data) {
        setData(res.data);
        setIsPaused(res.data.streamingStatus === "paused");
      }
    } catch (err) {
      logger.error("❌ Error fetching stream:", err);
    } finally {
      setLoading(false);
    }
  }, [CNR, post]);


useEffect(() => {
  if (!user?.id) return;

  const fetchToken = () => {
    api.get("/api/search/stream/hls-token")
      .then((res) => setHlsToken(res.data.token))
      .catch(console.error);
  };

  fetchToken(); // immediate
  const interval = setInterval(fetchToken, 30 * 60 * 1000); // refresh every 30min
  return () => clearInterval(interval);
}, [user?.id]);

  useEffect(() => {
    if (!CNR) {
      navigate(-1);
      return;
    }
    fetchStreamStatus();
  }, [CNR, fetchStreamStatus, navigate]);

// const securedStreamPath = data?.streamPath || "";

  /* ---------------------------------------------------------
      STREAM TIMER LOGIC
  --------------------------------------------------------- */
  useEffect(() => {
    if (!data?.streamStartAt || !data?.streamEndAt) return;
    if (data.status !== "rec+streaming") return;

    const startTime = new Date(data.streamStartAt).getTime();
    const endTime = new Date(data.streamEndAt).getTime();

    const check = () => {
      const now = Date.now();
      if (now >= endTime) {
        setStreamEnded(true);
        setIsLive(false);
      } else if (now >= startTime) {
        setIsLive(true);
      } else {
        setIsLive(false);
      }
    };

    check();
    const interval = setInterval(check, 2000);
    return () => clearInterval(interval);
  }, [data]);

  /* ---------------------------------------------------------
      SOCKET STREAM EVENTS
  --------------------------------------------------------- */
  useEffect(() => {
    if (!socket) return;

    const onStatus = ({ sessionId, status, streamingStatus }: any) => {
      if (sessionId !== CNR) return;

      // Pause — show overlay, mute player immediately
      if (streamingStatus === "paused" || status === "paused") {
        setIsPaused(true);
        return;
      }

      // Resume — lift overlay, unmute + seek to live edge
      // Both fields present distinguishes resume from initial "live" start
      if (status === "live" && streamingStatus === "live") {
        setIsPaused(false);
        return;
      }

      // All other status changes (initial start, scheduled, draining)
      // → refetch full data so timer/isLive recalculates correctly
      setStreamEnded(false);
      setIsLive(false);
      fetchStreamStatus();
    };

    const onStop = ({ sessionId }: any) => {
      if (sessionId === CNR) {
        setStreamEnded(true);
        setIsLive(false);
      }
    };

    recordingSocket.on("stream-status", onStatus);
    socket.on("stream_stop", onStop);

    return () => {
      recordingSocket.off("stream-status", onStatus);
      socket.off("stream_stop", onStop);
    };
  }, [socket, CNR, fetchStreamStatus]);

  /* ---------------------------------------------------------
      LEFT-BY ON PAGE UNLOAD
  --------------------------------------------------------- */
  useEffect(() => {
    if (!socket || !CNR || !user?.id) return;
    const payload = { sessionId: CNR, userId: user?.id };
    const handleUnload = () => {
      socket.emit("leftBy", payload);
      setIsLeft(true);
    };
    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, [socket, user]);

  /* ---------------------------------------------------------
      EMIT LEFT-BY ON ROUTE CHANGE
  --------------------------------------------------------- */
  useEffect(() => {
    return () => {
      if (socket && CNR && user?.id) {
        socket.emit("leftBy", { sessionId: CNR, userId: user.id });
        logger.log("🚪 LEFT BY SENT ON ROUTE CHANGE");
        setIsLeft(true);
      }
    };
  }, [socket, CNR, user]);

  useEffect(() => {
    if (!socket || !isLive || !watchedBy) return;
    socket.emit("watchedBy", watchedBy);
  }, [socket, isLive, watchedBy, isLeft]);

  console.log("hls token....",hlsToken);
  console.log("dataelekekle",data);

  /* ---------------------------------------------------------
      RENDER GUARDS
  --------------------------------------------------------- */
  if (loading)
    return (
      <div className="flex h-full items-center justify-center text-lg text-gray-500">
        Loading stream info...
      </div>
    );

  if (!data?.streamStatus) return <StreamingUnavailable />;
  if (streamEnded) return <StreamingEnd />;
  if (!data.streamStartAt) return <StreamNotStarted />;

  /* ---------------------------------------------------------
      MAIN RENDER
  --------------------------------------------------------- */


  return (
    <div className="w-full h-full">
      {isLive ? (
        <div className="relative w-full h-full">
          {/*
            Player stays ALWAYS mounted when isLive=true.
            Overlay sits on top — no unmount = no HLS buffer loss.
          */}
                <SecureStreamPlayer
        ref={playerRef}
        streamPath={data.streamPath} 
        hlsToken={hlsToken}            
        user={user?.name || ""}
        ip={ip}
        caseId={CNR || ""}
        mobile={mobile}
        courtName={data.highCourtName || ""}
        location={user?.location || ""}
        isPaused={isPaused}
      />

        </div>
      ) : (
        <StreamCountdown
          streamStartAt={data.streamStartAt}
          onCountdownEnd={() => setIsLive(true)}
        />
      )}
    </div>
  );
};

export default VideoPlayer;
